// Growth Intelligence - Referral & Growth Strategy Engine
// AI-powered EXECUTABLE growth plans for Goodwin Hospice and Home Health

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { 
  TrendingUp, Target, Users, DollarSign, Building2, Sparkles,
  Loader2, Lightbulb, Calendar, CheckCircle, AlertTriangle,
  BarChart3, Rocket, Award, MessageSquare, ArrowUpRight,
  FileText, RefreshCw, Clock, Zap, Heart, MapPin, Upload, X,
  FileSpreadsheet, ClipboardList, Download, FileType
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Focus area options
const FOCUS_AREAS = [
  { value: 'all', label: 'Complete Growth Plan', icon: Rocket, color: 'text-purple-400' },
  { value: 'referrals', label: 'Referral Development', icon: Users, color: 'text-blue-400' },
  { value: 'market', label: 'Market Expansion', icon: MapPin, color: 'text-green-400' },
  { value: 'staffing', label: 'Staff Capacity', icon: Heart, color: 'text-red-400' },
  { value: 'financial', label: 'Financial Growth', icon: DollarSign, color: 'text-emerald-400' },
  { value: 'competitive', label: 'Competitive Position', icon: Target, color: 'text-amber-400' }
];

export default function GrowthIntelligence() {
  const { token } = useAuth();
  
  // Strategy state
  const [focusArea, setFocusArea] = useState('all');
  const [serviceArea, setServiceArea] = useState('Georgia / Metro Atlanta area');
  const [additionalContext, setAdditionalContext] = useState('');
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Data state
  const [dataSummary, setDataSummary] = useState(null);
  const [capacityAnalysis, setCapacityAnalysis] = useState(null);
  const [quickInsights, setQuickInsights] = useState(null);
  const [growthIdeas, setGrowthIdeas] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  
  // UI state
  const [activeTab, setActiveTab] = useState('strategy');

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  // Fetch initial data
  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [summaryRes, capacityRes, insightsRes, ideasRes] = await Promise.all([
        fetch(`${API_URL}/api/growth-intelligence/data-summary`, { headers }),
        fetch(`${API_URL}/api/growth-intelligence/capacity-analysis`, { headers }),
        fetch(`${API_URL}/api/growth-intelligence/quick-insights`, { headers }),
        fetch(`${API_URL}/api/growth-intelligence/growth-ideas`, { headers })
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setDataSummary(data.summary);
      }
      if (capacityRes.ok) {
        const data = await capacityRes.json();
        setCapacityAnalysis(data.analysis);
      }
      if (insightsRes.ok) {
        const data = await insightsRes.json();
        setQuickInsights(data);
      }
      if (ideasRes.ok) {
        const data = await ideasRes.json();
        setGrowthIdeas(data.ideas || []);
      }
    } catch (error) {
      console.error('Failed to fetch growth data:', error);
    } finally {
      setDataLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle file drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedFile(e.target.files[0]);
    }
  };

  // Generate growth strategy with file upload
  const generateStrategy = async () => {
    setLoading(true);
    try {
      let response;
      
      if (uploadedFile) {
        // Use form data for file upload
        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('focus_area', focusArea);
        formData.append('service_area', serviceArea);
        if (additionalContext) {
          formData.append('additional_context', additionalContext);
        }
        
        response = await fetch(`${API_URL}/api/growth-intelligence/strategy-with-upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      } else {
        // Regular JSON request
        response = await fetch(`${API_URL}/api/growth-intelligence/strategy`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            focus_area: focusArea,
            additional_context: additionalContext,
            service_area: serviceArea
          })
        });
      }

      if (response.ok) {
        const data = await response.json();
        setStrategy(data.strategy);
        setActiveTab('results');
        toast.success('Executable growth plan generated!');
      } else {
        toast.error('Failed to generate plan');
      }
    } catch (error) {
      toast.error('Error generating growth plan');
    } finally {
      setLoading(false);
    }
  };

  // Copy strategy to clipboard
  const copyStrategy = () => {
    if (strategy?.strategy) {
      navigator.clipboard.writeText(strategy.strategy);
      toast.success('Plan copied to clipboard');
    }
  };

  // Download strategy as clean formatted document
  const downloadStrategy = () => {
    if (!strategy?.strategy) return;
    
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    
    // Clean up the markdown tables to be more readable
    let cleanContent = strategy.strategy
      .replace(/\|---+\|---+\|---+\|---+\|/g, '') // Remove table separators
      .replace(/^\|/gm, '• ') // Replace leading pipes with bullets
      .replace(/\|$/gm, '') // Remove trailing pipes
      .replace(/\s*\|\s*/g, ' → ') // Replace middle pipes with arrows
      .replace(/## /g, '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n') // Section dividers
      .replace(/# /g, '\n');
    
    const header = `
╔══════════════════════════════════════════════════════════════════╗
║          GOODWIN HOSPICE & HOME HEALTH                           ║
║          EXECUTABLE GROWTH PLAN                                  ║
╚══════════════════════════════════════════════════════════════════╝

Generated: ${formattedDate}
Service Area: ${strategy.service_area || 'Georgia / Metro Atlanta'}
Focus: ${strategy.focus_area || 'Complete Growth Plan'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

`;

    const footer = `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                     END OF GROWTH PLAN
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Generated by CareFlow Intelligence™
For internal use only - Goodwin Hospice and Home Health
`;

    const fullContent = header + cleanContent + footer;
    
    const blob = new Blob([fullContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Goodwin_Growth_Plan_${today.toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Plan downloaded');
  };

  // Export as professional PDF
  const exportPDF = () => {
    if (!strategy?.strategy) return;
    
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    
    // Parse the markdown content into structured HTML
    let htmlContent = strategy.strategy
      // Headers
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Convert markdown tables to HTML tables
      .replace(/\| Task \| Owner \| Deadline \| Success Metric \|/g, '<table class="action-table"><thead><tr><th>Task</th><th>Owner</th><th>Deadline</th><th>Success Metric</th></tr></thead><tbody>')
      .replace(/\| Initiative \| Owner \| Target \| Investment Required \|/g, '<table class="action-table"><thead><tr><th>Initiative</th><th>Owner</th><th>Target</th><th>Investment</th></tr></thead><tbody>')
      .replace(/\| Strategy \| Expected Outcome \| Risk \| Mitigation \|/g, '<table class="action-table"><thead><tr><th>Strategy</th><th>Expected Outcome</th><th>Risk</th><th>Mitigation</th></tr></thead><tbody>')
      .replace(/\| Metric \| Current \| 30-Day Target \| 90-Day Target \|/g, '<table class="metrics-table"><thead><tr><th>Metric</th><th>Current</th><th>30-Day</th><th>90-Day</th></tr></thead><tbody>')
      .replace(/\|[-]+\|[-]+\|[-]+\|[-]+\|/g, '') // Remove separator rows
      .replace(/^\| (.+?) \| (.+?) \| (.+?) \| (.+?) \|$/gm, '<tr><td>$1</td><td>$2</td><td>$3</td><td>$4</td></tr>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n- /g, '<li>')
      .replace(/<\/li>\n/g, '</li>');
    
    // Close any open tables
    htmlContent = htmlContent.replace(/<\/tr>\n<h/g, '</tr></tbody></table><h');
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Goodwin Growth Plan - ${formattedDate}</title>
          <style>
            @media print { 
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .no-print { display: none; }
            }
            @page { margin: 0.75in; }
            * { box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif; 
              max-width: 8.5in;
              margin: 0 auto;
              padding: 0;
              color: #1a1a1a;
              line-height: 1.5;
              font-size: 11pt;
            }
            .header {
              background: linear-gradient(135deg, #0f4c3a 0%, #1a6b50 100%);
              color: white;
              padding: 30px 40px;
              margin-bottom: 30px;
            }
            .header h1 {
              margin: 0;
              font-size: 24pt;
              font-weight: 700;
              letter-spacing: -0.5px;
            }
            .header .subtitle {
              margin: 5px 0 0 0;
              font-size: 12pt;
              opacity: 0.9;
            }
            .meta-row {
              display: flex;
              gap: 40px;
              padding: 15px 40px;
              background: #f8f9fa;
              border-bottom: 2px solid #1a6b50;
              margin-bottom: 25px;
            }
            .meta-item {
              display: flex;
              flex-direction: column;
            }
            .meta-label {
              font-size: 9pt;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .meta-value {
              font-size: 11pt;
              font-weight: 600;
              color: #1a1a1a;
            }
            .content {
              padding: 0 40px 40px 40px;
            }
            h2 {
              color: #0f4c3a;
              font-size: 14pt;
              font-weight: 700;
              margin: 30px 0 15px 0;
              padding-bottom: 8px;
              border-bottom: 2px solid #1a6b50;
            }
            h3 {
              color: #333;
              font-size: 12pt;
              font-weight: 600;
              margin: 20px 0 10px 0;
            }
            p {
              margin: 10px 0;
              color: #333;
            }
            .action-table, .metrics-table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0 25px 0;
              font-size: 10pt;
            }
            .action-table th, .metrics-table th {
              background: #0f4c3a;
              color: white;
              padding: 10px 12px;
              text-align: left;
              font-weight: 600;
            }
            .action-table td, .metrics-table td {
              padding: 10px 12px;
              border-bottom: 1px solid #e0e0e0;
              vertical-align: top;
            }
            .action-table tr:nth-child(even) td {
              background: #f8f9fa;
            }
            .action-table td:first-child {
              font-weight: 500;
              width: 35%;
            }
            .action-table td:nth-child(2) {
              color: #0f4c3a;
              font-weight: 600;
              width: 18%;
            }
            .action-table td:nth-child(3) {
              width: 15%;
            }
            .metrics-table td:not(:first-child) {
              text-align: center;
              font-weight: 600;
            }
            ul, ol {
              margin: 10px 0;
              padding-left: 25px;
            }
            li {
              margin: 5px 0;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e0e0e0;
              text-align: center;
              color: #666;
              font-size: 9pt;
            }
            .print-btn {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #0f4c3a;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
            }
            .print-btn:hover {
              background: #1a6b50;
            }
          </style>
        </head>
        <body>
          <button class="print-btn no-print" onclick="window.print()">Print / Save PDF</button>
          
          <div class="header">
            <h1>GOODWIN HOSPICE & HOME HEALTH</h1>
            <p class="subtitle">Executable Growth Plan</p>
          </div>
          
          <div class="meta-row">
            <div class="meta-item">
              <span class="meta-label">Generated</span>
              <span class="meta-value">${formattedDate}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Service Area</span>
              <span class="meta-value">${strategy.service_area || 'Georgia / Metro Atlanta'}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Focus</span>
              <span class="meta-value">${(strategy.focus_area || 'all').charAt(0).toUpperCase() + (strategy.focus_area || 'all').slice(1)}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Current ADC</span>
              <span class="meta-value">${strategy.system_data_snapshot?.census || 'N/A'}</span>
            </div>
          </div>
          
          <div class="content">
            ${htmlContent}
          </div>
          
          <div class="footer">
            <p><strong>CareFlow Intelligence™</strong> | Goodwin Hospice and Home Health</p>
            <p>This document is confidential and intended for internal planning purposes only.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('PDF ready - Click "Print / Save PDF" button');
  };

  // Get insight icon and color
  const getInsightStyle = (type) => {
    switch (type) {
      case 'success': return { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' };
      case 'warning': return { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' };
      case 'opportunity': return { icon: Lightbulb, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' };
      default: return { icon: Zap, color: 'text-slate-400', bg: 'bg-slate-500/10 border-slate-500/30' };
    }
  };

  // Get impact color
  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High': return 'bg-emerald-500/20 text-emerald-400';
      case 'Medium': return 'bg-amber-500/20 text-amber-400';
      case 'Low': return 'bg-slate-500/20 text-slate-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="growth-intelligence-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
            <TrendingUp className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-white">Growth Intelligence</h1>
            <p className="text-slate-400">AI-Powered Referral & Growth Strategy for Healthcare Operations</p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {!dataLoading && dataSummary && (
        <div className="max-w-7xl mx-auto mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Current ADC</p>
                  <p className="text-2xl font-bold text-white">{dataSummary.census?.current_adc || 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">30-Day Growth</p>
                  <p className={`text-2xl font-bold ${dataSummary.census?.net_growth_30_days >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {dataSummary.census?.net_growth_30_days >= 0 ? '+' : ''}{dataSummary.census?.net_growth_30_days || 0}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-emerald-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Capacity Used</p>
                  <p className="text-2xl font-bold text-white">
                    {capacityAnalysis?.capacity_utilization?.toFixed(0) || 0}%
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-400 opacity-50" />
              </div>
              <Progress 
                value={capacityAnalysis?.capacity_utilization || 0} 
                className="h-1.5 mt-2"
              />
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Growth Headroom</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    +{capacityAnalysis?.growth_headroom || 0}
                  </p>
                </div>
                <Rocket className="h-8 w-8 text-amber-400 opacity-50" />
              </div>
              <p className="text-xs text-slate-500 mt-1">patients before hiring</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-700 mb-6">
            <TabsTrigger 
              value="strategy" 
              className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
              data-testid="strategy-tab"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Strategy
            </TabsTrigger>
            <TabsTrigger 
              value="results" 
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400"
              disabled={!strategy}
              data-testid="results-tab"
            >
              <FileText className="h-4 w-4 mr-2" />
              Strategy Results
            </TabsTrigger>
            <TabsTrigger 
              value="ideas" 
              className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400"
              data-testid="ideas-tab"
            >
              <Lightbulb className="h-4 w-4 mr-2" />
              Growth Ideas ({growthIdeas.length})
            </TabsTrigger>
            <TabsTrigger 
              value="insights" 
              className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"
              data-testid="insights-tab"
            >
              <Zap className="h-4 w-4 mr-2" />
              Quick Insights
            </TabsTrigger>
          </TabsList>

          {/* Strategy Generation Tab */}
          <TabsContent value="strategy" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-white">
                      <ClipboardList className="h-5 w-5 text-emerald-400" />
                      Generate Executable Growth Plan
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Upload referral source lists, market data, or competitor info. Get a precise,
                      no-fluff action plan with specific owners, deadlines, and success metrics.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Service Area */}
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">
                        Service Area
                      </label>
                      <Input
                        value={serviceArea}
                        onChange={(e) => setServiceArea(e.target.value)}
                        placeholder="e.g., Georgia / Metro Atlanta area"
                        className="bg-slate-800 border-slate-600 text-white"
                        data-testid="service-area-input"
                      />
                    </div>

                    {/* Focus Area Selection */}
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Focus Area</label>
                      <Select value={focusArea} onValueChange={setFocusArea}>
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                          <SelectValue placeholder="Select focus area" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          {FOCUS_AREAS.map(area => (
                            <SelectItem key={area.value} value={area.value}>
                              <div className="flex items-center gap-2">
                                <area.icon className={`h-4 w-4 ${area.color}`} />
                                {area.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* File Upload Area */}
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">
                        Upload Data (Optional) - Referral lists, market reports, competitor data
                      </label>
                      <div
                        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                          dragActive 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : uploadedFile 
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="file-upload-zone"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".pdf,.docx,.xlsx,.xls,.csv,.txt"
                          onChange={handleFileSelect}
                        />
                        
                        {uploadedFile ? (
                          <div className="flex items-center justify-center gap-3">
                            <FileSpreadsheet className="h-8 w-8 text-emerald-400" />
                            <div className="text-left">
                              <p className="text-white font-medium">{uploadedFile.name}</p>
                              <p className="text-xs text-slate-400">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                              }}
                              className="text-slate-400 hover:text-red-400"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Upload className="h-8 w-8 text-slate-500 mx-auto" />
                            <p className="text-slate-400">Drop file here or click to upload</p>
                            <div className="flex flex-wrap justify-center gap-1">
                              {['PDF', 'Excel', 'CSV', 'Word'].map(type => (
                                <Badge key={type} variant="outline" className="text-xs border-slate-600 text-slate-500">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional Context */}
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">
                        Specific Goals or Challenges (Optional)
                      </label>
                      <Textarea
                        value={additionalContext}
                        onChange={(e) => setAdditionalContext(e.target.value)}
                        placeholder="e.g., Need to increase hospital referrals by 20%, facing competition from XYZ Hospice, looking to expand to Gwinnett County..."
                        className="min-h-[100px] bg-slate-800 border-slate-600 text-white"
                        data-testid="context-input"
                      />
                    </div>

                    {/* Generate Button */}
                    <Button
                      onClick={generateStrategy}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 h-12 text-lg font-semibold"
                      data-testid="generate-strategy-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Building Executable Plan...
                        </>
                      ) : (
                        <>
                          <Rocket className="h-5 w-5 mr-2" />
                          Generate Executable Growth Plan
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-slate-500 text-center">
                      Plan includes: Week 1 actions, 30/60/90 day milestones, assigned owners, measurable targets
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Data Preview */}
              <div className="space-y-4">
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-blue-400" />
                      Data Being Analyzed
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {dataLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                      </div>
                    ) : dataSummary ? (
                      <>
                        <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                          <span className="text-sm text-slate-400">Census Records</span>
                          <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                            Available
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                          <span className="text-sm text-slate-400">Staff: {dataSummary.staff?.total_active}</span>
                          <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                            Available
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                          <span className="text-sm text-slate-400">Quality Metrics</span>
                          <Badge variant="outline" className={dataSummary.has_quality_metrics ? 'border-emerald-500/50 text-emerald-400' : 'border-slate-500/50 text-slate-500'}>
                            {dataSummary.has_quality_metrics ? 'Available' : 'Not Tracked'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                          <span className="text-sm text-slate-400">Referral Sources</span>
                          <Badge variant="outline" className={dataSummary.has_referral_tracking ? 'border-emerald-500/50 text-emerald-400' : 'border-slate-500/50 text-slate-500'}>
                            {dataSummary.has_referral_tracking ? 'Available' : 'Not Tracked'}
                          </Badge>
                        </div>
                      </>
                    ) : (
                      <p className="text-slate-500 text-sm">No data available</p>
                    )}
                  </CardContent>
                </Card>

                <Alert className="bg-slate-900/80 border-slate-700/50">
                  <Lightbulb className="h-4 w-4 text-amber-400" />
                  <AlertTitle className="text-white">Pro Tip</AlertTitle>
                  <AlertDescription className="text-slate-400 text-sm">
                    Upload documents in Document Intelligence first, then reference insights here 
                    for more targeted growth recommendations.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </TabsContent>

          {/* Strategy Results Tab */}
          <TabsContent value="results" className="mt-0">
            {strategy && (
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="border-b border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-white">
                        <ClipboardList className="h-5 w-5 text-emerald-400" />
                        Goodwin Hospice & Home Health - Executable Growth Plan
                      </CardTitle>
                      <CardDescription className="text-slate-400">
                        Generated {new Date(strategy.timestamp).toLocaleString()} • 
                        Focus: {strategy.focus_area} • 
                        Service Area: {strategy.service_area || 'Georgia'}
                        {strategy.has_uploaded_document && ' • Includes uploaded data analysis'}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyStrategy}
                        className="border-slate-600 text-slate-300 hover:bg-slate-800"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadStrategy}
                        className="border-slate-600 text-slate-300 hover:bg-slate-800"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Text
                      </Button>
                      <Button
                        size="sm"
                        onClick={exportPDF}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      >
                        <FileType className="h-4 w-4 mr-1" />
                        Export PDF
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <ScrollArea className="h-[calc(100vh-380px)]">
                    <div className="prose prose-invert prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200 bg-transparent p-0 leading-relaxed">
                        {strategy.strategy}
                      </pre>
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Growth Ideas Tab */}
          <TabsContent value="ideas" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {growthIdeas.map((idea, index) => (
                <Card key={index} className="bg-slate-900/80 border-slate-700/50 hover:border-emerald-500/30 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="border-slate-600 text-slate-400">
                        {idea.category}
                      </Badge>
                      <Badge className={getImpactColor(idea.impact)}>
                        {idea.impact} Impact
                      </Badge>
                    </div>
                    <CardTitle className="text-white text-lg mt-2">{idea.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-400">{idea.description}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {idea.timeline}
                      </div>
                      <div className="flex items-center gap-1">
                        <Target className="h-3 w-3" />
                        Effort: {idea.effort}
                      </div>
                    </div>
                    
                    <div className="pt-2 border-t border-slate-700">
                      <p className="text-xs text-emerald-400 flex items-center gap-1">
                        <ArrowUpRight className="h-3 w-3" />
                        Expected: {idea.expected_result}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Quick Insights Tab */}
          <TabsContent value="insights" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Zap className="h-5 w-5 text-blue-400" />
                  Current Insights
                </h3>
                
                {quickInsights?.insights?.length > 0 ? (
                  quickInsights.insights.map((insight, index) => {
                    const style = getInsightStyle(insight.type);
                    return (
                      <Card key={index} className={`border ${style.bg}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <style.icon className={`h-5 w-5 mt-0.5 ${style.color}`} />
                            <div>
                              <Badge variant="outline" className="mb-2 border-slate-600 text-slate-400">
                                {insight.category}
                              </Badge>
                              <p className="text-white font-medium">{insight.insight}</p>
                              <p className="text-sm text-slate-400 mt-1">
                                <span className="text-emerald-400">Action:</span> {insight.action}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Card className="bg-slate-900/80 border-slate-700/50">
                    <CardContent className="p-8 text-center">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-400 opacity-50" />
                      <p className="text-slate-400">No critical insights at this time</p>
                      <p className="text-sm text-slate-500 mt-1">Your operations appear healthy</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-400" />
                  Data Snapshot
                </h3>
                
                {quickInsights?.data_snapshot && (
                  <Card className="bg-slate-900/80 border-slate-700/50">
                    <CardContent className="p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 rounded-lg bg-slate-800/50">
                          <p className="text-xs text-slate-500 uppercase">Current Census</p>
                          <p className="text-2xl font-bold text-white">{quickInsights.data_snapshot.current_adc}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-800/50">
                          <p className="text-xs text-slate-500 uppercase">Staff Count</p>
                          <p className="text-2xl font-bold text-white">{quickInsights.data_snapshot.staff_count}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-800/50">
                          <p className="text-xs text-slate-500 uppercase">Capacity Used</p>
                          <p className="text-2xl font-bold text-white">{quickInsights.data_snapshot.capacity_utilization}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-800/50">
                          <p className="text-xs text-slate-500 uppercase">Growth Room</p>
                          <p className="text-2xl font-bold text-emerald-400">+{quickInsights.data_snapshot.growth_headroom}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Button
                  onClick={fetchData}
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
